/**
 * Path to Performance Data Transformation Utilities
 * Transforms API responses to UI state format and vice versa
 */

import type {
  CurrentP2PData,
  ActionItemData,
  AssessmentData,
  CategoryLevelsResponse,
  CategoryActionItem,
} from "../../store/slices/pathToPerformanceSlice";
import {  DEFAULT_ACTION_OPTION, CATEGORY_TYPE_MAP } from "../../constants/pathToPerformance";

/**
 * Get category action items by category name
 * 
 * @param categoryName - Category name (e.g., "Sponsorship")
 * @param categoryLevels - Category levels from API
 * @returns Array of category action items or empty array
 */
const getCategoryItems = (
  categoryName: string,
  categoryLevels: CategoryLevelsResponse,
): CategoryActionItem[] => {
  const typeCode = CATEGORY_TYPE_MAP[categoryName] as keyof CategoryLevelsResponse;
  if (!typeCode) return [];
  
  return categoryLevels[typeCode] || [];
};

/**
 * Find category action item by action ID
 * 
 * @param items - Array of category action items
 * @param actionId - Action ID to find
 * @returns Found item or null
 */
const findItemByActionId = (
  items: CategoryActionItem[],
  actionId: number | null,
): CategoryActionItem | null => {
  if (!actionId || !items) return null;
  return items.find(item => item.actionNeededId === actionId) || null;
};

/**
 * Format action text from category action item
 * 
 * @param item - Category action item
 * @returns Formatted action text (e.g., "Sponsors identified (Level 1)")
 */
const formatActionText = (item: CategoryActionItem): string => {
  const levelText = item.actionNeededLvl === 99 ? "Level 99" : `Level ${item.actionNeededLvl}`;
  return `${item.actionNeededText} (${levelText})`;
};

/**
 * Transform P2P API data to UI action items array
 * Populates action items with category levels, action IDs, and notes from API response
 * 
 * @param p2pData - Current P2P data from API
 * @param categoryLevels - Category levels with action items from API
 * @returns Array of ActionItemData for UI
 * 
 * @example
 * ```typescript
 * const actionItems = transformP2PDataToActionItems(p2pData.current, categoryLevels);
 * dispatch(setActionItems(actionItems));
 * ```
 */
export const transformP2PDataToActionItems = (
  p2pData: CurrentP2PData,
  categoryLevels: CategoryLevelsResponse,
): ActionItemData[] => {
  const categories = [
    { name: "Sponsorship", level: p2pData.sponsershipCatLvl, actionId: p2pData.sponsershipActionId, notes: p2pData.sponsershipNotes },
    { name: "Leadership", level: p2pData.leadershipCatLvl, actionId: p2pData.leadershipActionId, notes: p2pData.leadershipNotes },
    { name: "Training", level: p2pData.trainingCatLvl, actionId: p2pData.trainingActionId, notes: p2pData.trainingNotes },
    { name: "Team Process", level: p2pData.processCatLvl, actionId: p2pData.processActionId, notes: p2pData.processNotes },
    { name: "Team Engagement", level: p2pData.teamEngCatLvl, actionId: p2pData.teamEngActionId, notes: p2pData.teamEngNotes },
    { name: "Use of Tools", level: p2pData.toolsUseCatLvl, actionId: p2pData.toolsUseActionId, notes: p2pData.toolsUseNotes },
    { name: "Goals and Performance", level: p2pData.goalsPerfCatLvl, actionId: p2pData.goalsPerfActionId, notes: p2pData.goalsPerfNotes },
  ];

  return categories.map((category, index) => {
    const categoryItems = getCategoryItems(category.name, categoryLevels);
    const selectedItem = findItemByActionId(categoryItems, category.actionId);
    const actionNeeded = selectedItem ? formatActionText(selectedItem) : DEFAULT_ACTION_OPTION;

    return {
      id: String(index + 1),
      category: category.name,
      categoryLevel: category.level ?? 0,
      actionNeeded,
      notes: category.notes ?? "",
    };
  });
};

/**
 * Transform P2P API data to UI assessments array
 * 
 * @param currentP2P - Current period P2P data from API
 * @param previousP2P - Previous period P2P data from API
 * @returns Array of AssessmentData for UI
 * 
 * @example
 * ```typescript
 * const assessments = transformP2PDataToAssessments(p2pData.current, p2pData.previous);
 * dispatch(setAssessments(assessments));
 * ```
 */
export const transformP2PDataToAssessments = (
  currentP2P: CurrentP2PData,
  previousP2P: { p2pRating: number | null; p2pAssesmentNotes: string | null; p2pAssesmentDt: string | null } | null,
): AssessmentData[] => {
  return [
    {
      id: "1",
      period: "Current",
      teamRating: currentP2P.p2pRating ?? 0,
      assessmentDate: currentP2P.p2pAssesmentDt ?? "",
      assessmentNotes: currentP2P.p2pAssesmentNotes ?? "",
    },
    {
      id: "2",
      period: "Previous",
      teamRating: previousP2P?.p2pRating ?? 0,
      assessmentDate: previousP2P?.p2pAssesmentDt ?? "",
      assessmentNotes: previousP2P?.p2pAssesmentNotes ?? "",
    },
  ];
};

/**
 * Update performance data with P2P rating
 * Helper to update the team rating field from P2P data
 * 
 * @param currentRating - Current team rating
 * @param p2pRating - P2P rating from API
 * @returns Updated rating value
 */
export const updatePerformanceRating = (
  currentRating: string | number,
  p2pRating: number | null,
): string | number => {
  if (p2pRating !== null && p2pRating !== undefined) {
    return p2pRating;
  }
  return currentRating;
};
